import React, { useEffect, useMemo, useState } from 'react'
import { TourStep, WtnPages } from 'constants/guided-tours'
import Joyride, { CallBackProps, Props as JoyrideProps, ACTIONS, EVENTS } from 'react-joyride'
import { getKeysForStepsToComplete, storeKeysForCompletedSteps } from 'utils/local-storage'

const defaultStyles = {
  options: {
    zIndex: 10000,
  },
  buttonNext: {
    borderRadius: '20px',
    padding: '12px',
    paddingLeft: '16px',
    paddingRight: '16px',
    backgroundColor: '#dc3741',
    outline: 'none',
    fontSize: '15px',
    fontWeight: 500,
  },
  buttonBack: {
    color: '#dc3741',
    fontSize: '15px',
    fontWeight: 500,
  },
  spotlight: {
    borderRadius: 0,
  },
}

interface Props extends JoyrideProps {
  delay?: number
}

interface TourState {
  stepIndex: number
  isRunning: boolean
  isComplete: boolean
}

const GuidedTour = (props: Props) => {
  const [canTourStart, setCanTourStart] = useState(false)

  useEffect(() => {
    setTimeout(() => {
      setCanTourStart(true)
    }, props.delay || 300)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <Joyride {...props} run={props.run && canTourStart} />
}

export const useGuidedTour = (
  tennisId: string,
  page: WtnPages,
  steps: TourStep[],
  options: { autoStart: boolean; callback?: (data: CallBackProps) => void } = { autoStart: true }
) => {
  const { autoStart, callback } = options
  const tourStepsToComplete = useMemo(() => {
    if (!tennisId) return []
    const stepsToComplete: TourStep[] = []
    const keysForStepsToComplete = getKeysForStepsToComplete(tennisId, page)

    if (!keysForStepsToComplete.length) return stepsToComplete

    steps.forEach(step => {
      if (keysForStepsToComplete.includes(step.name)) stepsToComplete.push(step)
    })

    return stepsToComplete
  }, [steps, page, tennisId])
  const [tourState, setTourState] = useState<TourState>({
    stepIndex: 0,
    isRunning: false,
    isComplete: false,
  })

  const handleJoyrideCallback =
    callback ??
    ((data: CallBackProps) => {
      const { action, type } = data

      switch (action) {
        case ACTIONS.NEXT:
          if (type === EVENTS.STEP_AFTER) {
            setTourState(prev => ({ ...prev, stepIndex: prev.stepIndex + 1 }))
          } else if (type === EVENTS.TOUR_END) {
            storeKeysForCompletedSteps(
              tennisId,
              page,
              tourStepsToComplete.map(step => step.name)
            )
            setTourState(prev => ({ ...prev, stepIndex: 0, isRunning: false, isComplete: true }))
          }
          break
        case ACTIONS.PREV:
          if (type === EVENTS.STEP_AFTER)
            setTourState(prev => ({ ...prev, stepIndex: prev.stepIndex - 1 }))
          break
        case ACTIONS.CLOSE:
          if (type === EVENTS.STEP_AFTER) {
            setTourState(prev => ({ ...prev, stepIndex: prev.stepIndex + 1 }))
          } else if (type === EVENTS.TOUR_END) {
            storeKeysForCompletedSteps(
              tennisId,
              page,
              tourStepsToComplete.map(step => step.name)
            )
            setTourState(prev => ({ ...prev, stepIndex: 0, isRunning: false, isComplete: true }))
          }
          break
        case ACTIONS.RESET:
        case ACTIONS.SKIP:
          storeKeysForCompletedSteps(
            tennisId,
            page,
            tourStepsToComplete.map(step => step.name)
          )
          setTourState(prev => ({ ...prev, stepIndex: 0, isRunning: false, isComplete: true }))
          break
      }
    })

  const props: JoyrideProps = {
    steps: tourStepsToComplete.map(step => step.joyrideStep),
    run: tourStepsToComplete.length > 0 && tourState.isRunning,
    scrollOffset: tourStepsToComplete[tourState.stepIndex]?.joyrideOptions?.scrollOffset || 20,
    disableOverlayClose: true,
    showProgress: true,
    continuous: true,
    showSkipButton: true,
    spotlightPadding:
      tourStepsToComplete[tourState.stepIndex]?.joyrideOptions?.spotlightPadding || 0,
    callback: handleJoyrideCallback,
    styles: defaultStyles,
    stepIndex: tourState.stepIndex,
  }

  useEffect(() => {
    if (!tourStepsToComplete.length) {
      setTourState(prev => {
        return { ...prev, isRunning: false, isComplete: true }
      })
    }
  }, [tourStepsToComplete])

  useEffect(() => {
    if (autoStart) {
      setTourState(prev => {
        return { ...prev, isRunning: true }
      })
    }
  }, [autoStart])

  return {
    currentStep: tourStepsToComplete[tourState.stepIndex],
    props,
    GuidedTour,
    tourState,
    setTourState,
  }
}
